import { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { parentName: "", studentName: "", grade: "", phone: "", message: "" };

const inputClass = (hasError) =>
  `min-h-[48px] w-full rounded-xl border bg-white px-4 py-3 text-base text-brand-ink transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
    hasError ? "border-red-500" : "border-brand-line"
  }`;

export const EnquiryForm = ({ source = "contact" }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (form.parentName.trim().length < 2) errs.parentName = "Please enter the parent or guardian name.";
    if (form.studentName.trim().length < 2) errs.studentName = "Please enter the student name.";
    if (!form.grade.trim()) errs.grade = "Please enter the class or grade you are interested in.";
    if (!/^[0-9+\-\s()]{7,20}$/.test(form.phone.trim()))
      errs.phone = "Please enter a valid phone number.";
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, {
        parent_name: form.parentName.trim(),
        student_name: form.studentName.trim(),
        grade: form.grade.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        source,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="enquiry-success-message"
        role="status"
        className="flex flex-col items-start gap-4 rounded-3xl border border-brand-green/30 bg-green-50 p-8"
      >
        <CheckCircle2 className="h-10 w-10 text-brand-green" aria-hidden="true" />
        <p className="font-display text-xl font-bold text-brand-ink">
          Thank you. Your enquiry has been received.
        </p>
        <p className="text-base text-slate-700">
          Our team will contact you using the details provided.
        </p>
        <button
          type="button"
          data-testid="enquiry-send-another-button"
          onClick={() => {
            setForm(initial);
            setStatus("idle");
          }}
          className="min-h-[44px] rounded-full border border-brand bg-white px-6 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-brand-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="enquiry-form"
      onSubmit={onSubmit}
      noValidate
      className="space-y-5"
    >
      {status === "error" && (
        <div
          data-testid="enquiry-error-message"
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          We couldn't submit your enquiry. Please check your information or contact the school directly.
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="parent-name" className="mb-1.5 block text-sm font-semibold text-brand-ink">
            Parent/Guardian Name *
          </label>
          <input
            id="parent-name"
            data-testid="enquiry-parent-name-input"
            type="text"
            value={form.parentName}
            onChange={set("parentName")}
            aria-invalid={!!errors.parentName}
            aria-describedby={errors.parentName ? "parent-name-error" : undefined}
            className={inputClass(errors.parentName)}
            placeholder="e.g. Muhammad Ahmad"
            autoComplete="name"
          />
          {errors.parentName && (
            <p id="parent-name-error" data-testid="enquiry-parent-name-error" className="mt-1.5 text-sm text-red-600">
              {errors.parentName}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="student-name" className="mb-1.5 block text-sm font-semibold text-brand-ink">
            Student Name *
          </label>
          <input
            id="student-name"
            data-testid="enquiry-student-name-input"
            type="text"
            value={form.studentName}
            onChange={set("studentName")}
            aria-invalid={!!errors.studentName}
            aria-describedby={errors.studentName ? "student-name-error" : undefined}
            className={inputClass(errors.studentName)}
            placeholder="e.g. Ali Ahmad"
          />
          {errors.studentName && (
            <p id="student-name-error" data-testid="enquiry-student-name-error" className="mt-1.5 text-sm text-red-600">
              {errors.studentName}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="grade" className="mb-1.5 block text-sm font-semibold text-brand-ink">
            Class/Grade Interested In *
          </label>
          <input
            id="grade"
            data-testid="enquiry-grade-input"
            type="text"
            value={form.grade}
            onChange={set("grade")}
            aria-invalid={!!errors.grade}
            aria-describedby={errors.grade ? "grade-error" : undefined}
            className={inputClass(errors.grade)}
            placeholder="e.g. Class 3"
          />
          {errors.grade && (
            <p id="grade-error" data-testid="enquiry-grade-error" className="mt-1.5 text-sm text-red-600">
              {errors.grade}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="phone" className="mb-1.5 block text-sm font-semibold text-brand-ink">
            Phone Number *
          </label>
          <input
            id="phone"
            data-testid="enquiry-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "phone-error" : undefined}
            className={inputClass(errors.phone)}
            placeholder="e.g. 0345 6232508"
            autoComplete="tel"
          />
          {errors.phone && (
            <p id="phone-error" data-testid="enquiry-phone-error" className="mt-1.5 text-sm text-red-600">
              {errors.phone}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="message" className="mb-1.5 block text-sm font-semibold text-brand-ink">
          Message
        </label>
        <textarea
          id="message"
          data-testid="enquiry-message-input"
          rows={4}
          value={form.message}
          onChange={set("message")}
          className={`${inputClass(false)} min-h-[120px] resize-y`}
          placeholder="Any questions about admission, classes or timings..."
        />
      </div>

      <button
        type="submit"
        data-testid="enquiry-submit-button"
        disabled={status === "sending"}
        className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-3 text-base font-semibold text-white shadow-sm transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            Sending enquiry...
          </>
        ) : (
          <>
            <Send className="h-4 w-4" aria-hidden="true" />
            Send Enquiry
          </>
        )}
      </button>
    </form>
  );
};
